import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Transition, StateService } from '@uirouter/core';
import { CardModule } from 'primeng/card';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { GlobalToastMessageService } from '@client/app/shared-modules/global-toast/services/global-toast-message.service';
import { PerformanceProfilesRepo } from '@client/app/feature-modules/admin/modules/admin-performance-profiles/services/performance-profiles.repo';
import { PagePerformanceProfile } from '@client/app/models/page-performance/profile.model';
import { PagePerformanceLocationService } from '@client/app/modules/page-performance/services/page-performance-location/page-performance-location.service';
import { PagePerformanceProfileObsService } from '@client/app/modules/page-performance/services/profile-obs/profile-obs.service';
import { PAGE_PERFORMANCE_CONNECTION_PROFILES } from '@client/app/modules/page-performance/constants/page-performance-connection.-profiles';
import { PagePerformanceService } from '@client/app/modules/page-performance/services/page-performance/page-performance.service';
import { Button } from 'primeng/button';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AdminLayoutService } from '@client/app/feature-modules/admin/services/admin-layout.service';
import { PageLayoutComponent } from '@client/app/feature-modules/shared/components/page-layout/page-layout.component';

@Component({
    selector: 'mon-admin-add-edit-performance-profile',
    standalone: true,
    imports: [
        CommonModule,
        TranslocoModule,
        CardModule,
        InputTextModule,
        FormsModule,
        SelectModule,
        Button,
        PageLayoutComponent,
    ],
    templateUrl: './admin-add-edit-performance-profile.component.html',
    styleUrls: ['./admin-add-edit-performance-profile.component.scss'],
})
export class AdminAddEditPerformanceProfileComponent implements OnInit {
    constructor (
        private readonly transition: Transition,
        private readonly performanceProfilesRepo: PerformanceProfilesRepo,
        private readonly pagePerformanceLocationService: PagePerformanceLocationService,
        private readonly pagePerformanceProfileObsService: PagePerformanceProfileObsService,
        private readonly stateService: StateService,
        private readonly pagePerformanceService: PagePerformanceService,
        private readonly translocoService: TranslocoService,
        private readonly globalToastMessageService: GlobalToastMessageService,
        private readonly adminLayoutService: AdminLayoutService,
    ) {}

    isEditMode: boolean = false;
    profile: PagePerformanceProfile = {
        id: 0,
        name: '',
        description: '',
        device: '',
        location: '',
        cpu_slowdown_multiplier: 1,
        download_throughput: 0,
        upload_throughput: 0,
        request_latency: 0,
        round_trip_time: 0,
        throughput: 0,
        customer_id: 0,
        created_at: '',
        updated_at: '',
        archived_at: null,
        connectionName: '',
        deviceName: '',
        countryCode: '',
        cityName: '',
        countryName: '',
    } as PagePerformanceProfile & {
        connectionName: string;
        deviceName: string;
        countryCode: string;
        cityName: string;
        countryName: string;
    };
    deviceOptions: { label: string; value: string; icon: string }[] = [];
    currentConnectionProfile: string = '';
    connectionProfiles = [...PAGE_PERFORMANCE_CONNECTION_PROFILES];
    locationOptions = this.pagePerformanceLocationService.getLocations();
    currentLocation: string = '';
    saving: boolean = false;

    ngOnInit (): void {
        this.deviceOptions = [
            { label: this.translocoService.translate('adminPerformanceProfiles.addEdit.form.deviceOptions.desktop'), value: 'desktop', icon: 'pi pi-desktop' },
            { label: this.translocoService.translate('adminPerformanceProfiles.addEdit.form.deviceOptions.mobile'), value: 'mobile', icon: 'pi pi-mobile' },
        ];
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const id = this.transition.params().id;
        this.isEditMode = Boolean(id);
        this.setBreadcrumbs();

        if (this.isEditMode) {
            this.performanceProfilesRepo.get(id).then(profile => {
                this.profile = profile;
                if (this.profile.archived_at && this.profile.archived_at !== null) {
                    this.stateService.go('base.customer.admin.performance-profiles');
                    return;
                }
                this.currentLocation = profile.location || this.locationOptions[0].key;
                this.setupConnectionProfile();
            });
        } else {
            this.checkProfileLimitReached().then(limitReached => {
                if (limitReached) {
                    this.stateService.go('base.customer.admin.performance-profiles');
                    return;
                }
            }).catch(() => {
                this.stateService.go('base.customer.admin.performance-profiles');
            });
            this.setupConnectionProfile();
        }
    }

    setBreadcrumbs (): void {
        this.adminLayoutService.setBreadcrumbs({
            uiStates: [
                {
                    label: this.translocoService.translate('admin.performanceProfiles.table.breadcrumbLabel'),
                    state: 'base.customer.admin.performance-profiles',
                },
                {
                    label: this.isEditMode
                        ? this.translocoService.translate('admin.performanceProfiles.form.edit.breadcrumbLabel')
                        : this.translocoService.translate('admin.performanceProfiles.form.add.breadcrumbLabel'),
                    state: 'base.customer.admin.performance-profile-form',
                },
            ],
        });
    }

    checkProfileLimitReached (): Promise<boolean> {
        const maxProfilesAvailable = this.pagePerformanceService.getMaxProfileCount();
        return this.performanceProfilesRepo.getAll({
            page: 1,
            page_size: 1,
            archived: false,
        }).then(profilesResponse => {
            const totalProfilesAvailable = profilesResponse ? profilesResponse.total || 0 : 0;
            return totalProfilesAvailable >= maxProfilesAvailable;
        });
    }

    setupConnectionProfile (): void {
        if (this.isEditMode) {
            const target = this.connectionProfiles.find((connectionProfile) => {
                for (const key in connectionProfile.settings) {
                    if (connectionProfile.settings[key] !== this.profile[key]) {
                        return false;
                    }
                }
                return true;
            });
            if (target) {
                this.currentConnectionProfile = target.name;
            }
        }
    }

    saveProfile (): void {
        this.saving = true;
        const newProfile: PagePerformanceProfile = structuredClone(this.profile);
        newProfile.location = this.currentLocation;
        this.mergeConnectionProfile(this.currentConnectionProfile, newProfile);
        let promise: Promise<PagePerformanceProfile | void>;
        if (this.isEditMode) {
            promise = this.performanceProfilesRepo.update(newProfile).then(() => {
                this.pagePerformanceProfileObsService.updateProfile(newProfile);
            });
        } else {
            promise = this.performanceProfilesRepo.create(newProfile).then(() => {
                this.pagePerformanceProfileObsService.addProfile(newProfile);
            });
        }
        promise.then(() => {
            this.saving = false;
            let message = '';
            if (this.isEditMode) {
                message = this.translocoService.translate('adminPerformanceProfiles.addEdit.form.editSuccess{{profileName}}', {
                    profileName: newProfile.name,
                });
            } else {
                message = this.translocoService.translate('adminPerformanceProfiles.addEdit.form.createSuccess{{profileName}}', {
                    profileName: newProfile.name,
                });
            }
            this.globalToastMessageService.success(message);
            this.close();
        }).catch(() => {
            this.saving = false;
            this.globalToastMessageService.error(
                this.translocoService.translate('adminPerformanceProfiles.addEdit.form.saveError{{profileName}}', {
                    profileName: newProfile.name,
                }),
            );
        });
    }

    close (): void {
        this.stateService.go('base.customer.admin.performance-profiles');
    }

    mergeConnectionProfile (connectionProfileName: string, profile: PagePerformanceProfile): void {
        const connectionProfile = this.connectionProfiles.find((entry) => entry.name === connectionProfileName);
        if (connectionProfile) {
            Object.assign(profile, connectionProfile.settings);
        }
    }

    getPageHeader (): string {
        const label = this.isEditMode
            ? this.translocoService.translate('adminPerformanceProfiles.addEdit.editLabel')
            : this.translocoService.translate('adminPerformanceProfiles.addEdit.addLabel');
        const common = this.translocoService.translate('adminPerformanceProfiles.addEdit.pageHeaderCommon');
        return `${label} ${common}`;
    }
}
